import React, { useState, useEffect } from 'react';
import { Eye, EyeOff, Loader2, ArrowLeft, User, Mail, Lock, Phone, Calendar, Globe, CheckCircle } from 'lucide-react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useQueryClient } from 'react-query';
import styles from './Signup.module.css';
import Footer from '../../common/UI/Footer';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import useAuthStore from '../../store/authStore';
import { initializeApp } from 'firebase/app';
import { getAuth, signInWithPopup, GoogleAuthProvider } from 'firebase/auth';
import axios from 'axios';

// Initialize Firebase
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGE_SENDING_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID
};

// Initialize Firebase app
const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const googleProvider = new GoogleAuthProvider();

const Signup = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'user',
    phone: '',
    gender: '',
    dob: '',
    language: '',
    agreeToTerms: false
  });
  
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [googleError, setGoogleError] = useState(null);
  const [isSigningUp, setIsSigningUp] = useState(false);
  const navigate = useNavigate();
  const { signup, isLoading, error } = useAuth();
  const { setUser } = useAuthStore();
  const queryClient = useQueryClient();

  // Show error toast when error changes
  useEffect(() => {
    if (error) {
      toast.error(error?.message || 'An error occurred during sign up');
      setIsSigningUp(false);
    }
  }, [error]);

  useEffect(() => {
    if (googleError) {
      toast.error(googleError);
    }
  }, [googleError]);

  // Custom signup function that handles navigation directly
  const handleCustomSignup = async (userData) => {
    try {
      setIsSigningUp(true);
      
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL || 'http://localhost:4000/api'}/auth/signup`,
        userData,
        { withCredentials: true }
      );

      if (response.data && response.data.user) {
        console.log('Signup successful, setting user and navigating to subscription plans...');
        
        // Set user data in query cache
        queryClient.setQueryData('authStatus', response.data);
        
        // Set user in auth store
        setUser(response.data.user);
        
        toast.success('Signup successful! Redirecting to plans...');
        
        // Navigate immediately using window.location.replace to bypass route guards
        window.location.replace('/subscription-plans');
      }
    } catch (error) {
      console.error('Signup error:', error);
      const errorMessage = error.response?.data?.message || 'Failed to sign up';
      toast.error(errorMessage);
      setErrors({ submit: errorMessage });
      setIsSigningUp(false);
    }
  };
  
  const validateStep1 = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }
    
    return newErrors;
  };

  const validateStep2 = () => {
    const newErrors = {};
    
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    }
    
    if (!formData.gender) {
      newErrors.gender = 'Gender is required';
    }
    
    if (!formData.dob) {
      newErrors.dob = 'Date of birth is required';
    } else {
      // Calculate age from date of birth
      const today = new Date();
      const birthDate = new Date(formData.dob);
      const age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();
      
      // Adjust age if birthday hasn't occurred this year
      const actualAge = monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate()) ? age - 1 : age;
      
      if (actualAge < 10) {
        newErrors.dob = 'You must be at least 10 years old to register';
      }
    }
    
    if (!formData.language) {
      newErrors.language = 'Language is required';
    }
    
    if (!formData.agreeToTerms) {
      newErrors.agreeToTerms = 'You must agree to the terms';
    }
    
    return newErrors;
  };

  const handleContinue = () => {
    const newErrors = validateStep1();
    
    if (Object.keys(newErrors).length === 0) {
      setCurrentStep(2);
      setErrors({});
    } else {
      setErrors(newErrors);
      toast.error('Please fix the errors in the form');
    }
  };

  const handleBack = () => {
    setCurrentStep(1);
    setErrors({});
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = validateStep2();
    
    if (Object.keys(newErrors).length === 0) {
      console.log('Starting signup process...');
      setIsSigningUp(true);
      // Call signup mutation - React Query will handle success/error
      handleCustomSignup(formData);
    } else {
      setErrors(newErrors);
      toast.error('Please fix the errors in the form');
    }
  };
  
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Handle Google Sign In
  const handleGoogleSignIn = async () => {
    try {
      setIsGoogleLoading(true);
      setGoogleError(null);
      
      const result = await signInWithPopup(auth, googleProvider);
      // Get the ID token from the user credential
      const idToken = await result.user.getIdToken();
      
      // Send the token to your backend
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:4000/api'}/auth/google`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ idToken }),
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Failed to authenticate with Google');
      }

      const data = await response.json();
      toast.success('Google login successful! Redirecting...');
      setTimeout(() => {
        // Use window.location.href to force navigation and bypass route guards
        window.location.href = '/subscription-plans';
      }, 500);
    } catch (error) {
      console.error('Google auth error:', error);
      setGoogleError(error.message || 'Failed to authenticate with Google');
    } finally {
      setIsGoogleLoading(false);
    }
  };

  const renderStepIndicator = () => (
    <div className={styles.stepIndicator}>
      <div className={`${styles.step} ${currentStep >= 1 ? styles.active : ''}`}>
        {currentStep > 1 ? <CheckCircle size={20} /> : '1'}
      </div>
      <div className={`${styles.step} ${currentStep >= 2 ? styles.active : ''}`}>
        {currentStep > 2 ? <CheckCircle size={20} /> : '2'}
      </div>
    </div>
  );

  const renderStep1 = () => (
    <>
      <h2>Create Your Account</h2>
      <p className={styles.stepDescription}>Join our community and start your journey</p>
      
      <div className={styles.inputGroup}>
        <div className={styles.inputWrapper}>
          <User size={20} className={styles.inputIcon} />
          <input
            type="text"
            name="name"
            placeholder="Enter your full name"
            value={formData.name}
            onChange={handleChange}
            className={errors.name ? styles.errorInput : ''}
            disabled={isLoading}
          />
        </div>
        {errors.name && <span className={styles.errorText}>{errors.name}</span>}
      </div>

      <div className={styles.inputGroup}>
        <div className={styles.inputWrapper}>
          <Mail size={20} className={styles.inputIcon} />
          <input
            type="email"
            name="email"
            placeholder="Enter your email address"
            value={formData.email}
            onChange={handleChange}
            className={errors.email ? styles.errorInput : ''}
            disabled={isLoading}
          />
        </div>
        {errors.email && <span className={styles.errorText}>{errors.email}</span>}
      </div>

      <div className={styles.inputGroup}>
        <div className={styles.inputWrapper}>
          <Lock size={20} className={styles.inputIcon} />
          <div className={styles.passwordInput}>
            <input
              type={showPassword ? "text" : "password"}
              name="password"
              placeholder="Create a strong password"
              value={formData.password}
              onChange={handleChange}
              className={errors.password ? styles.errorInput : ''}
              disabled={isLoading}
            />
            <button
              type="button"
              className={styles.eyeIcon}
              onClick={() => setShowPassword(!showPassword)}
              disabled={isLoading}
            >
              {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
            </button>
          </div>
        </div>
        {errors.password && <span className={styles.errorText}>{errors.password}</span>}
      </div>

      <button 
        type="button" 
        className={`${styles.submitButton} ${isLoading ? styles.loading : ''}`}
        disabled={isLoading}
        onClick={handleContinue}
      >
        Continue to Next Step
      </button>
    </>
  );

  const renderStep2 = () => (
    <>
      <h2>Complete Your Profile</h2>
      <p className={styles.stepDescription}>Help us personalize your experience</p>
      
      <div className={styles.inputGroup}>
        <div className={styles.inputWrapper}>
          <Phone size={20} className={styles.inputIcon} />
          <input
            type="tel"
            name="phone"
            placeholder="Enter your phone number"
            value={formData.phone}
            onChange={handleChange}
            className={errors.phone ? styles.errorInput : ''}
            disabled={isLoading}
          />
        </div>
        {errors.phone && <span className={styles.errorText}>{errors.phone}</span>}
      </div>

      <div className={styles.inputGroup}>
        <div className={styles.inputWrapper}>
          <User size={20} className={styles.inputIcon} />
          <select
            name="gender"
            value={formData.gender}
            onChange={handleChange}
            className={errors.gender ? styles.errorInput : ''}
            disabled={isLoading}
          >
            <option value="">Select your gender</option>
            <option value="male">Male</option>
            <option value="female">Female</option>
            <option value="other">Other</option>
          </select>
        </div>
        {errors.gender && <span className={styles.errorText}>{errors.gender}</span>}
      </div>

      <div className={styles.inputGroup}>
        <div className={styles.inputWrapper}>
          <Calendar size={20} className={styles.inputIcon} />
          <input
            type="date"
            name="dob"
            value={formData.dob}
            onChange={handleChange}
            className={errors.dob ? styles.errorInput : ''}
            disabled={isLoading}
          />
        </div>
        {errors.dob && <span className={styles.errorText}>{errors.dob}</span>}
      </div>

      <div className={styles.inputGroup}>
        <div className={styles.inputWrapper}>
          <Globe size={20} className={styles.inputIcon} />
          <select
            name="language"
            value={formData.language}
            onChange={handleChange}
            className={errors.language ? styles.errorInput : ''}
            disabled={isLoading}
          >
            <option value="">Select your preferred language</option>
            <option value="english">English</option>
            <option value="hindi">Hindi</option>
            <option value="punjabi">Punjabi</option>
            <option value="gujarati">Gujarati</option>
            <option value="marathi">Marathi</option>
            <option value="bengali">Bengali</option>
            <option value="tamil">Tamil</option>
            <option value="telugu">Telugu</option>
            <option value="kannada">Kannada</option>
            <option value="malayalam">Malayalam</option>
          </select>
        </div>
        {errors.language && <span className={styles.errorText}>{errors.language}</span>}
      </div>

      <div className={styles.checkboxGroup}>
        <input
          type="checkbox"
          name="agreeToTerms"
          checked={formData.agreeToTerms}
          onChange={handleChange}
          id="terms"
          disabled={isLoading}
        />
        <label htmlFor="terms">
          I agree to the{' '}
          <span 
            className={styles.linkText}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              // Navigate to terms page or open terms modal
              window.open('/terms', '_blank');
            }}
          >
            Terms of Service
          </span>
          {' '}and{' '}
          <span 
            className={styles.linkText}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              // Navigate to privacy policy page or open privacy modal
              window.open('/privacy-policy', '_blank');
            }}
          >
            Privacy Policy
          </span>
        </label>
        {errors.agreeToTerms && <span className={styles.errorText}>{errors.agreeToTerms}</span>}
      </div>

      <div className={styles.buttonGroup}>
        <button 
          type="button" 
          className={styles.backButton}
          onClick={handleBack}
          disabled={isLoading}
        >
          <ArrowLeft size={16} />
          Back
        </button>
        <button 
          type="submit" 
          className={`${styles.submitButton} ${isLoading ? styles.loading : ''}`}
          disabled={isLoading}
        >
          {isLoading ? (
            <span className={styles.loadingWrapper}>
              <Loader2 className={styles.spinner} size={20} />
              Creating Account...
            </span>
          ) : (
            'Create Account'
          )}
        </button>
      </div>
    </>
  );

  return (
    <>
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
      
      <div className={styles.container}>
        <div className={styles.formCard}>
          {/* Toggle Button Group for Sign In / Sign Up */}
          <div className={styles.toggleButtonGroup}>
            <button
              type="button"
              className={styles.toggleButton}
              onClick={() => navigate('/signin')}
            >
              Sign In
            </button>
            <button
              type="button"
              className={`${styles.toggleButton} ${styles.active}`}
              disabled
            >
              Sign Up
            </button>
          </div>
          <form onSubmit={handleSubmit} className={styles.form}>
            {currentStep === 1 && renderStep1()}
            {currentStep === 2 && renderStep2()}

            {currentStep === 1 && (
              <>
                <div className={styles.divider}>
                  <span>or continue with</span>
                </div>

                <div className={styles.socialButtons}>
                  <button 
                    type="button" 
                    className={styles.googleButton}
                    disabled={isLoading || isGoogleLoading}
                    onClick={handleGoogleSignIn}
                  >
                    {isGoogleLoading ? (
                      <span className={styles.loadingWrapper}>
                        <Loader2 className={styles.spinner} size={20} />
                        Connecting...
                      </span>
                    ) : (
                      <>
                        <img src="./images/logo/googlelogo.png" className={styles.logoImage} alt="google" />
                        Continue with Google
                      </>
                    )}
                  </button>
                </div>

                <p className={styles.loginLink}>
                  Already have an account? <span onClick={()=>navigate('/signin')}>Sign in</span>
                </p>
              </>
            )}
          </form>
        </div>
      </div>
      <Footer/>
    </>
  );
};

export default Signup;